import { EFeedbackPriorityType, IFeedback, IFeedbackUserInfo, IUserInfoDto } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ChangeEvent, PureComponent, RefObject, createRef } from "react";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { FeedBackService } from "../../../services/feedback-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import TrashAttachmentSvg from "../../../svgs/small-icons/trash-attachment.svg";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./new-ticket.scss";

/** Send is not a save: the button says what it is doing in its own words. */
const SEND_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Sending", success: "Sent" };

export interface INewTicketState {
  subject: string;
  message: string;
  isLoading: boolean;
  attachments: string[];
  /** Where Send stands. Owned by `SaveStatus`, never set directly. */
  sendStatus: KuikaButtonStatus;
}

export interface INewTicketProps {
  sd: IScreenDesignContextState;
  profileInfo: IUserInfoDto;
  onTicketSent: () => void;
}

class NewTicket extends PureComponent<INewTicketProps, INewTicketState> {
  private ALLOWED_MIME_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/svg+xml"];

  private MAX_SUBJECT_LENGTH: number = 100;

  private MAX_MESSAGE_LENGTH: number = 1000;

  private MAX_ATTACHMENTS: number = 4;

  private fileInputRef: RefObject<HTMLInputElement>;

  /**
   * Send's state machine. A sent ticket replaces this composer with its own thread, so the ✓ is
   * held on Send first and `onTicketSent` runs after it — the composer never leaves before its
   * confirmation has been seen. `canStart` keeps a second Send (a duplicate ticket) out meanwhile.
   */
  private readonly sendStatus: SaveStatus = new SaveStatus((sendStatus: KuikaButtonStatus): void => {
    this.setState({ sendStatus });
  });

  constructor(props: INewTicketProps) {
    super(props);
    this.state = {
      subject: "",
      message: "",
      isLoading: false,
      attachments: [],
      sendStatus: "idle"
    };
    this.fileInputRef = createRef();
  }

  componentWillUnmount(): void {
    this.sendStatus.dispose();
  }

  componentDidMount() {
    if (this.props.sd.tempHelpdeskNewTicketValues) {
      this.setState({
        subject: this.props.sd.tempHelpdeskNewTicketValues.subject ?? "",
        message: this.props.sd.tempHelpdeskNewTicketValues.message ?? "",
        attachments: this.props.sd.tempHelpdeskNewTicketValues.attachments ?? []
      });
    }
  }

  private onChangeSubject = (_e: ChangeEvent<HTMLInputElement>, value: string | number): void => {
    const subject: string = String(value);
    if (subject.length > this.MAX_SUBJECT_LENGTH) return;
    this.setState({ subject });
  };

  private onChangeMessage = (_e: ChangeEvent<HTMLTextAreaElement>, value: string | number): void => {
    const message: string = String(value);
    if (message.length > this.MAX_MESSAGE_LENGTH) return;
    this.setState({ message });
  };

  private onClickUpload = (): void => {
    if (this.state.attachments.length >= this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        `You can only upload ${this.MAX_ATTACHMENTS} images at most. Remove one to add another.`
      );
      return;
    }
    this.fileInputRef.current?.click();
  };

  private onClickSend = async () => {
    try {
      if (_.isEmpty(this.state.subject) || _.isEmpty(this.state.message) || !this.sendStatus.canStart) {
        return;
      }

      this.sendStatus.start();
      this.setState({ isLoading: true });
      const userInfo: IFeedbackUserInfo = {
        userId: this.props.profileInfo.id,
        userName: `${this.props.profileInfo.firstName} ${this.props.profileInfo.lastName}`,
        userEmail: this.props.profileInfo.username,
        workspaceId: this.props.profileInfo.kDomain_Id,
        workspaceName: this.props.profileInfo.lastUsedDomainName,
        projectId: this.props.sd.project == undefined || this.props.sd.project == null ? "" : this.props.sd.project.id,
        projectName:
          this.props.sd.project == undefined || this.props.sd.project == null ? "" : this.props.sd.project.name,
        screenId: this.props.sd.sdId,
        screenName:
          this.props.sd.screenList.filter((e) => e.id === this.props.sd.sdId)[0] == undefined
            ? ""
            : this.props.sd.screenList.filter((e) => e.id === this.props.sd.sdId)[0].name
      };

      const payload: IFeedback = {
        Subject: this.state.subject,
        Description: this.state.message,
        Priority: EFeedbackPriorityType.Medium,
        UserInfo: userInfo,
        Images: this.state.attachments || []
      };

      const response = await FeedBackService.CreateTicket(payload);

      if (response?.status === 200) {
        this.sendStatus.succeed((): void => this.props.onTicketSent());
      } else {
        this.sendStatus.fail();
      }
    } catch (e) {
      this.sendStatus.fail();
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  private getBase64 = (file: File): Promise<string> => {
    const reader: FileReader = new FileReader();

    return new Promise((resolve) => {
      reader.onload = (e) => {
        if (!e.target) return;
        if (typeof e.target.result !== "string") return;
        resolve(e.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  private onClickDeleteAttachment = (e: React.MouseEvent, attachment: string) => {
    e.stopPropagation();
    const attachments: string[] = this.state.attachments.filter((a) => a !== attachment);
    this.setState({
      attachments
    });
    this.updateTicketValues(this.state.subject, this.state.message, attachments);
  };

  private onDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  private onDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  private processFileList = async (fileList: FileList): Promise<void> => {
    if (this.state.attachments.length >= this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        `You can't upload any more images. You can only upload ${this.MAX_ATTACHMENTS} images at most. Consider removing some of the images if you want to upload another one.`
      );
      return;
    }

    const files: File[] = Array.from(fileList).filter((file) => {
      const isAllowedMimeType: boolean = this.ALLOWED_MIME_TYPES.includes(file.type);

      if (!isAllowedMimeType) {
        KMainFunctions.displayWarningNotification(`File type is not supported for file "${file.name}."`);
      }

      return isAllowedMimeType;
    });

    if (fileList.length > files.length) {
      KMainFunctions.displayWarningNotification(
        "Only images with the following extensions are supported: png, jpg, jpeg, svg."
      );
    }

    if (files.length <= 0) return;

    if (this.state.attachments.length + files.length > this.MAX_ATTACHMENTS) {
      KMainFunctions.displayWarningNotification(
        `You can only upload ${this.MAX_ATTACHMENTS} images at most. Files that exceed the limit will be ignored.`
      );
    }

    const droppedFiles: string[] = await Promise.all(files.map((f) => this.getBase64(f)));
    const attachments: string[] = [...this.state.attachments];

    droppedFiles.forEach((url: string) => {
      if (attachments.length >= this.MAX_ATTACHMENTS) return;
      if (attachments.includes(url)) return;
      attachments.push(url);
    });

    this.setState({ attachments });
    this.updateTicketValues(this.state.subject, this.state.message, attachments);
  };

  private onDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    await this.processFileList(e.dataTransfer.files);
  };

  private onChangeFileInput = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    await this.processFileList(e.target.files);
    e.target.value = "";
  };

  /**
   * Half-written tickets survive closing the panel. Both handlers persist from component state
   * rather than the event target: the value has already been through the length clamp, and the
   * state is the same whichever shape the input wrapper hands back.
   */
  private onBlurSubject = (): void => {
    this.updateTicketValues(this.state.subject, this.state.message, this.state.attachments);
  };

  private onBlurMessage = (): void => {
    this.updateTicketValues(this.state.subject, this.state.message, this.state.attachments);
  };

  /**
   * Wipes the form back to blank — fields, attachments, and the saved draft — without leaving
   * the composer. This used to be Cancel, which backed all the way out to a ticket (or the
   * empty state); that exit stopped making sense once the composer became the default right
   * pane on every open — closing Helpdesk and reopening it already lands back here with an
   * empty or restored draft (see `openHelpdesk`), so the only thing left worth a button is
   * discarding what was typed.
   */
  private onClickClear = (): void => {
    this.setState({ subject: "", message: "", attachments: [] });
    this.updateTicketValues("", "", []);
  };

  /**
   * Screenshot-first flow: the fastest path from a bug to a ticket is a region grab straight into
   * the clipboard, and without this the only way in was saving the image to disk first. Text
   * pastes are untouched — the handler stands down unless the clipboard actually carries files.
   */
  private onPaste = async (e: React.ClipboardEvent<HTMLElement>): Promise<void> => {
    const files: FileList | undefined = e.clipboardData?.files;
    if (!files || files.length === 0) return;
    e.preventDefault();
    await this.processFileList(files);
  };

  private updateTicketValues(subject: string, message: string, attachments: string[]) {
    this.props.sd.setTempHelpdeskNewTicketValues({
      subject,
      message,
      attachments
    });
  }

  render() {
    const isEmpty: boolean = _.isEmpty(this.state.subject) || _.isEmpty(this.state.message);
    const isAttachmentLimitReached: boolean = this.state.attachments.length >= this.MAX_ATTACHMENTS;

    return (
      <div className="new-ticket-container" onPaste={this.onPaste}>
        {/*
          The composer is a FORM, not a message box: labelled fields, and the three things a
          support agent asks for anyway said once, where the answer is being typed. It replaced a
          ~230px illustration over a void — that space was buying no information, and the two
          unlabelled fields read as one undivided box.
        */}
        <div className="new-ticket-scroll">
          <div className="new-ticket-column">
            <header className="new-ticket-heading">
              <h5 className="new-ticket-heading-title">Tell us what went wrong</h5>
              <p className="new-ticket-heading-description">
                Support replies by mail; the ticket stays here so you can follow it.
              </p>
            </header>

            <KuikaSpinnerSmall spinning={this.state.isLoading}>
              <div
                className="new-ticket"
                onDragEnter={this.onDragEnter}
                onDragOver={this.onDragOver}
                onDrop={this.onDrop}
              >
                <div className="new-ticket-field">
                  <label className="new-ticket-label" htmlFor="newTicketSubject">
                    Title
                  </label>
                  <KuikaInput
                    id="newTicketSubject"
                    data-testid="ticketTitleInput"
                    className="new-ticket-subject"
                    placeholder="One line: what broke"
                    value={this.state.subject}
                    onChange={this.onChangeSubject}
                    onBlur={this.onBlurSubject}
                  />
                </div>

                <div className="new-ticket-field">
                  <div className="new-ticket-label-row">
                    <label className="new-ticket-label" htmlFor="newTicketMessage">
                      What happened
                    </label>
                    {/*
                      The counter belongs to the field it counts. In the action row it read as a
                      property of SEND, and sat furthest from the text it was measuring.
                    */}
                    <span className="new-ticket-message-count">
                      {this.state.message.length}/{this.MAX_MESSAGE_LENGTH}
                    </span>
                  </div>
                  <KuikaInput
                    id="newTicketMessage"
                    data-testid="ticketMessageInput"
                    className="new-ticket-message"
                    type="textarea"
                    resizable={false}
                    placeholder="Write the details of the support request"
                    value={this.state.message}
                    onChange={this.onChangeMessage}
                    onBlur={this.onBlurMessage}
                  />
                  <ul className="new-ticket-prompts">
                    <li className="new-ticket-prompt">Steps you took</li>
                    <li className="new-ticket-prompt">What you expected</li>
                    <li className="new-ticket-prompt">What happened instead</li>
                  </ul>
                </div>

                <div className="new-ticket-field">
                  <label className="new-ticket-label">Screenshots</label>
                  {/*
                    A drop target the size of the affordance it describes, rather than a button
                    beside a hint. The whole composer still accepts a drop; this is where the
                    invitation lives, and it is also the click and paste target.
                  */}
                  <button
                    type="button"
                    data-testid="uploadImage"
                    className="new-ticket-dropzone"
                    disabled={isAttachmentLimitReached}
                    onClick={this.onClickUpload}
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDrop={this.onDrop}
                  >
                    <KuikaSvg name="image" width={20} height={20} />
                    <span className="new-ticket-dropzone-label">
                      Drop a screenshot, paste, or <span className="new-ticket-dropzone-browse">browse</span>
                    </span>
                    <span className="new-ticket-dropzone-hint">max {this.MAX_ATTACHMENTS} · PNG, JPG, SVG</span>
                  </button>

                  <input
                    className="new-ticket-upload-file-input"
                    data-testid="newTicketFileInput"
                    ref={this.fileInputRef}
                    type="file"
                    multiple
                    accept={this.ALLOWED_MIME_TYPES.join(",")}
                    onChange={this.onChangeFileInput}
                  />

                  {this.state.attachments.length > 0 && (
                    <div className="new-ticket-attachments">
                      {this.state.attachments.map((attachment: string) => (
                        <button
                          key={attachment}
                          className="new-ticket-attachment-button"
                          onClick={(e) => this.onClickDeleteAttachment(e, attachment)}
                        >
                          <img className="new-ticket-attachment-image" src={attachment} alt="" />
                          <span className="new-ticket-attachment-trash-backdrop">
                            <TrashAttachmentSvg />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="new-ticket-actions">
                  <span className="new-ticket-actions-spacer" />
                  <KuikaButton
                    data-testid="ticketClearButton"
                    type="text"
                    color="neutral"
                    size="md"
                    onClick={this.onClickClear}
                  >
                    Clear
                  </KuikaButton>
                  <KuikaButton
                    data-testid="sendButton"
                    size="md"
                    uppercase
                    status={this.state.sendStatus}
                    statusLabels={SEND_STATUS_LABELS}
                    onClick={this.onClickSend}
                    disabled={isEmpty}
                  >
                    Send
                  </KuikaButton>
                </div>
              </div>
            </KuikaSpinnerSmall>
          </div>
        </div>
      </div>
    );
  }
}

export default NewTicket;
